import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import {
  ArrowLeft, User, Music, StickyNote, Star, Mail, Phone,
  Sparkles, FileText, Loader2, Users2,
} from "lucide-react";

export default function CharacterDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/characters/${id}/detail`);
        if (!cancelled) setState({ loading: false, data });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load character");
        nav("/scenes");
      }
    })();
    return () => { cancelled = true; };
  }, [id, nav]);

  if (state.loading) return <div className="text-zinc-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  const { character, cast, scenes, notes, script } = state.data || {};
  if (!character) return null;

  return (
    <div className="space-y-6" data-testid="character-detail-page">
      <button onClick={() => nav(-1)} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <header className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 flex flex-col sm:flex-row gap-5">
        <Avatar url={cast?.headshot_url} name={cast?.name || character.name} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Character</div>
          <h1 className="mt-1 font-display text-4xl tracking-tight">{character.name}</h1>
          {character.voice_type && <div className="mt-1 text-sm text-zinc-400">{character.voice_type}</div>}
          {character.description && <p className="mt-2 text-sm text-zinc-400 max-w-2xl">{character.description}</p>}
          {cast && (
            <div className="mt-4 rounded-lg border border-gold/30 bg-gold/5 p-3 inline-block">
              <div className="text-[10px] uppercase tracking-[0.24em] text-gold">Cast</div>
              <div className="mt-1 text-lg font-display text-zinc-100">{cast.name}</div>
              <div className="mt-1 text-[11px] text-zinc-400 flex items-center gap-3">
                {cast.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {cast.email}</span>}
                {cast.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {cast.phone}</span>}
              </div>
            </div>
          )}
        </div>
        {script && (
          <div className="rounded-lg border border-white/10 bg-[#121212] p-4 text-right shrink-0">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Script</div>
            <div className="mt-1 text-3xl font-display text-gold">{script.lines_count}</div>
            <div className="text-[11px] text-zinc-400">lines · {script.scenes?.length || 0} scenes</div>
            <Link to="/script" className="mt-2 inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-gold">
              <FileText className="w-3 h-3" /> Open script
            </Link>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel icon={Star} title="Scenes & Songs" empty="Not appearing in any scenes yet.">
          {(scenes || []).length > 0 && (
            <ul className="divide-y divide-white/5">
              {scenes.map((s) => (
                <li key={s.id} className="p-3">
                  <div className="text-sm text-zinc-100">{s.is_song && <Music className="w-3.5 h-3.5 text-gold inline mr-1" />} {s.name}</div>
                  <div className="text-[11px] text-zinc-500">Act {s.act} · #{s.position ?? "—"}</div>
                </li>
              ))}
            </ul>
          )}
          {script?.scenes?.length > 0 && (
            <div className="p-3 border-t border-white/5 text-[11px] text-zinc-500">
              Script appearances: {script.scenes.join(", ")}
            </div>
          )}
        </Panel>

        <Panel icon={StickyNote} title="Cast Notes" empty="No notes tagged for this character yet.">
          {(notes || []).length > 0 && (
            <ul className="divide-y divide-white/5">
              {notes.slice(0, 20).map((n) => (
                <li key={n.id} className="p-3">
                  <div className="text-sm text-zinc-100 whitespace-pre-wrap">{n.body || n.note || n.text}</div>
                  <div className="text-[11px] text-zinc-500 mt-1">{n.created_at ? fmtDate(n.created_at) : ""}{n.author_name ? ` · ${n.author_name}` : ""}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {script?.sample_line && (
          <Panel icon={Sparkles} title="First line (from script)" empty="">
            <div className="p-4 text-sm text-zinc-200 italic">&ldquo;{script.sample_line}&rdquo;</div>
          </Panel>
        )}

        <Panel icon={Users2} title="Placeholder — Blocking · Costume · Mic · Photos" empty="">
          <div className="p-4 text-xs text-zinc-500 leading-relaxed">
            These sections will populate as you build blocking notation, costume tracking, mic assignments,
            and character photos. The character deep-view is designed to grow — new modules plug straight in.
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Panel({ icon: Icon, title, children, empty }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] overflow-hidden">
      <div className="p-3 border-b border-white/5 flex items-center gap-2">
        <div className="w-7 h-7 rounded grid place-items-center bg-gold/10 text-gold border border-gold/20">
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="font-display text-base">{title}</div>
      </div>
      {children || (empty ? <div className="p-6 text-xs text-zinc-500 text-center">{empty}</div> : null)}
    </div>
  );
}

function Avatar({ url, name }) {
  const initials = (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  return url ? (
    <img src={url} alt={name} className="w-24 h-24 rounded-full object-cover bg-[#121212] border border-white/10 shrink-0" />
  ) : (
    <div className="w-24 h-24 rounded-full bg-[#121212] border border-white/10 grid place-items-center text-2xl font-display text-zinc-400 shrink-0">
      {initials}
    </div>
  );
}
